import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatTimeRange, paysByLink, shortBookingReference } from "@majourneys/shared";
import { formatMad } from "../lib/format";
import type { BookingDetail } from "../api/bookings.api";
import { PW, pewterButton, pewterPanel } from "../internal-ui/pewter-shell";
import { pewterDangerSoft } from "../internal-ui/pewter-modal";
import { CancelBookingModal } from "./cancel-booking-modal";
import { ModifyBookingModal } from "./modify-booking-modal";

import { Icon } from "@majourneys/icons";
import { BEVEL_INSET_WHITE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

/**
 * "View details" opens here rather than navigating to the thank-you page
 * (owner, 2026-09-10: "voir details must as well appear as a sheet").
 *
 * It is a peek, not a second copy of that page: everything it shows is already
 * in the `liveBooking` query the status strip polls, so opening it costs no
 * request and can never disagree with the strip above it. The full page is one
 * tap away at the bottom for the things this does not carry — the map, the
 * receipt, the review.
 *
 * Modify and cancel are here rather than only there (owner, 2026-09-10: "how
 * can he cancel or modify from the sheet"). Both reuse the modals the
 * thank-you page already opens, and — importantly — the same two rules it
 * gates them on, so the sheet can never offer an action the page would refuse:
 * modify while the booking is still PENDING, cancel until it is cancelled,
 * rejected or completed. Once confirmed, changing a date means talking to the
 * provider, which is what the WhatsApp line under the buttons says.
 */
interface BookingDetailsSheetProps {
  booking: BookingDetail;
  /** The thank-you page for this booking; the sheet's own footer link. */
  fullDetailsHref: string;
  whatsappHref: string;
  onClose: () => void;
  /** Called after a successful cancel, so the caller can drop the strip. */
  onCancelled?: () => void;
}

export function BookingDetailsSheet({
  booking,
  fullDetailsHref,
  whatsappHref,
  onClose,
  onCancelled,
}: BookingDetailsSheetProps) {
  const { t, i18n } = useTranslation();
  const [showCancel, setShowCancel] = useState(false);
  const [showModify, setShowModify] = useState(false);

  // Escape closes, and the page underneath does not scroll while it is open —
  // the sheet sits over a page that is itself scrollable, and without this the
  // background slides away under your finger.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const { reservation: r, pricing } = booking;
  const isTableBooking = booking.activity?.tableBooking === true;
  const byLink = paysByLink(booking.activity?.slug);
  const isCancelled = booking.status === "CANCELLED" || booking.status === "REJECTED";
  const isConfirmed =
    booking.status === "CONFIRMED" || booking.status === "PAID" || booking.status === "COMPLETED";
  // Same two rules as thank-you-page, deliberately duplicated in the same
  // shape rather than loosened: modify only while nobody has confirmed
  // anything yet, cancel until the booking is already over or gone.
  const canModify = booking.status === "PENDING";
  const canCancel =
    booking.status !== "CANCELLED" &&
    booking.status !== "REJECTED" &&
    booking.status !== "COMPLETED";

  const statusLabel = isCancelled
    ? t("activity.cancelled", { defaultValue: "Cancelled" })
    : isConfirmed
      ? t("activity.confirmed", { defaultValue: "Confirmed" })
      : t("activity.awaiting_confirmation", { defaultValue: "Awaiting confirmation" });

  const dateLabel = (() => {
    // The reservation date is a plain calendar day. Parsed as UTC and rendered
    // in a western timezone it slides back a day, which on a booking is the
    // difference between the right morning and the wrong one — so build the
    // Date from its parts rather than letting Date parse the string.
    const [y, m, d] = r.date.split("-").map(Number);
    if (!y || !m || !d) return r.date;
    return new Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date(y, m - 1, d));
  })();

  // "2 Adults · 1 Children", built from the labels the rest of the funnel
  // already ships in all seven locales rather than three new plural keys for
  // one line on one sheet.
  const guestParts = [
    r.adults > 0 ? `${r.adults} ${t("activity.adults")}` : null,
    r.children > 0 ? `${r.children} ${t("activity.children")}` : null,
    r.infants > 0 ? `${r.infants} ${t("activity.infants")}` : null,
  ].filter(Boolean) as string[];

  const rows: { key: string; label: string; value: string; ltr?: boolean }[] = [
    { key: "date", label: t("activity.date"), value: dateLabel },
    ...(r.time ? [{ key: "time", label: t("activity.time"), value: formatTimeRange(r.time, r.timeEnd), ltr: true }] : []),
    ...(guestParts.length > 0
      ? [{ key: "guests", label: t("activity.guests"), value: guestParts.join(" · ") }]
      : []),
    ...(booking.package?.name
      ? [{ key: "pack", label: t("activity.step_package", { defaultValue: "Package" }), value: booking.package.name }]
      : []),
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("activity.view_details", { defaultValue: "View details" })}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[rgba(4,0,51,0.45)] p-3 pb-[max(12px,env(safe-area-inset-bottom))] backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(86dvh_/_var(--mj-ui-zoom,1))] w-full overflow-y-auto rounded-[26px] p-4 pb-5 sm:max-w-md"
        style={pewterPanel(26, { gap: 4 })}
      >
        {/* The grab handle, phone only — on a centred dialog it would be a
            handle for something that does not drag.

            The sheet floats clear of all four edges rather than sitting flush
            to the bottom: the pewter frame is a band with its own rounded
            corners, and anchored to the viewport its bottom two were sliced
            off against the screen edge. */}
        <span
          aria-hidden
          className="mx-auto mb-3 block h-1 w-10 rounded-full bg-[rgba(17,14,57,0.15)] sm:hidden"
        />

        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
              {t("activity.booking", { defaultValue: "Booking" })}
            </p>
            <p className="mt-0.5 [overflow-wrap:anywhere] font-mono text-[13px] font-bold text-[#110E39]">
              {shortBookingReference(booking.reference) || booking.reference}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={pewterButton}
          >
            <Icon name="ri-close-line" className="text-[16px] text-[#110E39]" aria-hidden />
          </button>
        </div>

        <span
          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-sans text-[11px] font-bold"
          style={
            isCancelled
              ? { background: PW.redTint, color: PW.red }
              : isConfirmed
                ? { background: PW.greenTint, color: PW.green }
                : {
                    background: "linear-gradient(135deg, #EFC98C 0%, #E5B265 52%, #D89C4A 100%)",
                    color: PW.navy,
                  }
          }
        >
          <Icon
            name={isCancelled ? "ri-close-circle-line" : isConfirmed ? "ri-check-line" : "ri-time-line"}
            className="text-[13px]"
            aria-hidden
          />
          {statusLabel}
        </span>

        <p className="mt-4 font-heading text-[17px] font-bold leading-[1.25] text-[#110E39]">
          {booking.activity.title}
        </p>
        {booking.activity.cityName && (
          <p className="mt-0.5 font-sans text-[12px] text-[#5a5d77]">{booking.activity.cityName}</p>
        )}

        <dl className="mt-4 overflow-hidden rounded-2xl" style={{ background: PW.softTint }}>
          {rows.map((row, i) => (
            <div
              key={row.key}
              className="flex items-baseline justify-between gap-4 px-4 py-2.5"
              style={i === 0 ? undefined : { boxShadow: `inset 0 1px 0 ${PW.hairline}` }}
            >
              <dt className="shrink-0 font-sans text-[11.5px] font-semibold text-[#5a5d77]">
                {row.label}
              </dt>
              <dd className="text-end font-sans text-[12.5px] font-bold text-[#110E39]">
                {row.ltr ? <span dir="ltr">{row.value}</span> : row.value}
              </dd>
            </div>
          ))}
        </dl>

        {/* A table is held, not sold: we bill nothing for it, so a total here
            is a zero that reads as a free meal (owner, 2026-09-15). */}
        {isTableBooking ? (
          <p className="mt-4 font-sans text-[12px] text-[#5a5d77]">
            {t("activity.table_pay_restaurant", {
                                defaultValue: "We hold the table. You pay the restaurant for what you order.",
                              })}
          </p>
        ) : (
          <>
            <div className="mt-4 flex items-baseline justify-between gap-4">
              <span className="font-sans text-[12.5px] font-bold text-[#110E39]">
                {byLink
                  ? t("activity.total_paid_online")
                  : t("booking.total_due_on_the_day", { defaultValue: "Total due on the day" })}
              </span>
              <span className="font-heading text-[18px] font-bold text-[#110E39]">
                {formatMad(pricing.total, i18n.language)}
              </span>
            </div>
            {/* Activities are pay-on-arrival; nothing was charged here. The
                desert camps are stays, paid from a link our team emails. */}
            <p className="mt-1 font-sans text-[11.5px] text-[#5a5d77]">
              {byLink
                ? t("activity.pay_by_link_sub")
                : t("booking.pay_on_arrival", { defaultValue: "Pay on arrival" })}
            </p>
          </>
        )}

        {(canModify || canCancel) && (
          <div className="mt-4 flex items-center gap-2">
            {canModify && (
              <button
                type="button"
                onClick={() => setShowModify(true)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold text-[#110E39]"
                style={{
                  background: BEVEL_WHITE_FACE,
                  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}, 0 6px 14px rgba(17,14,57,0.07)`,
                }}
              >
                <Icon name="ri-edit-line" className="text-[14px]" aria-hidden />
                {t("activity.modify_booking", { defaultValue: "Modify booking" })}
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                onClick={() => setShowCancel(true)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold"
                style={pewterDangerSoft}
              >
                <Icon name="ri-close-circle-line" className="text-[14px]" aria-hidden />
                {t("activity.cancel_booking", { defaultValue: "Cancel booking" })}
              </button>
            )}
          </div>
        )}
        {/* Confirmed means a provider is holding a slot for you, so a date
            change is a conversation, not a form. Same wording the full page
            uses when it hides its own Modify pill. */}
        {!canModify && canCancel && (
          <p className="mt-2 font-sans text-[11.5px] text-[#5a5d77]">
            <Icon name="ri-lock-line" className="me-1 align-middle" aria-hidden />
            {t("activity.modify_locked_after_confirm", {
              defaultValue:
                "Booking is confirmed — message us on WhatsApp to change date, time, or guests.",
            })}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 font-sans text-[12.5px] font-bold text-white"
            style={{
              background: "linear-gradient(160deg, #45DD8B 0%, #25D366 55%, #1DB458 100%)",
              boxShadow:
                "0 8px 18px rgba(37,211,102,0.35), inset 0 1px 0 rgba(255,255,255,0.28)",
            }}
          >
            <Icon name="ri-whatsapp-line" className="text-[15px]" aria-hidden />
            {t("activity.chat_with_us", { defaultValue: "Chat with us" })}
          </a>
          <a
            href={fullDetailsHref}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold text-[#110E39]"
            style={{
              background: BEVEL_WHITE_FACE,
              boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}, 0 6px 14px rgba(17,14,57,0.07)`,
            }}
          >
            {t("activity.open_full_details", { defaultValue: "Full details" })}
            <Icon name="ri-arrow-right-up-line" className="text-[14px]" aria-hidden />
          </a>
        </div>
      </div>

      {/* Siblings of the sheet body, not children of it: the body scrolls and
          is clipped to 88vh, and a modal rendered inside would have been
          trapped in it. They still sit inside this overlay, whose own click
          closes the sheet — hence the stop: without it, dismissing a modal
          would take the sheet behind it down too. */}
      <div onClick={(e) => e.stopPropagation()}>
      {showCancel && (
        <CancelBookingModal
          bookingReference={booking.reference}
          activityTitle={booking.activity.title || ""}
          date={r.date}
          payByLink={byLink}
          /* Idea 86 (owner, 2026-10-25): a new day before a cancel. Modify
             while pending, the same rule as the pill above; WhatsApp after. */
          onChangeDate={
            canModify
              ? () => {
                  setShowCancel(false);
                  setShowModify(true);
                }
              : undefined
          }
          changeDateByWhatsApp
          onClose={() => setShowCancel(false)}
          onCancelled={() => {
            onCancelled?.();
            onClose();
          }}
        />
      )}
      {showModify && (
        <ModifyBookingModal booking={booking} onClose={() => setShowModify(false)} />
      )}
      </div>
    </div>
  );
}
